import { segmentVsAabbs } from '../../../engine/collision.js';

const BASE_PRESSURE_INTERVAL = 4.5;
const MIN_PRESSURE_INTERVAL = 0.85;
const PRESSURE_RATE_STEP_SECONDS = 30;
const PRESSURE_RATE_STEP_MULTIPLIER = 1.04;
const PRESSURE_MIN_PLAYER_DISTANCE = 4.5;
const PRESSURE_SPAWN_RADII = [4.8, 6.4, 8.2, 10.2];
const PRESSURE_SPAWN_TYPES = ['drone', 'drone', 'drone', 'minmon', 'enforcer', 'zealot', 'zilua'];
const ROOM_KILL_TARGETS = [40, 52, 64, 78, 92, 108, 124, 142, 160, 180];
const BASE_PRESSURE_ALIVE_CAP = 14;
const MAX_PRESSURE_ALIVE_CAP = 34;
const BASE_TOTAL_ENEMY_CAP = 28;
const MAX_TOTAL_ENEMY_CAP = 46;

export function isRegularExitPressureRoom(game) {
  const roomNumber = (game?.roomIndex ?? 0) + 1;
  const isBossRoom = Boolean(game?._bossRooms?.[roomNumber]) || Boolean(game?._isBossRoomActive?.());
  return game?.runMode === 'run' && !isBossRoom;
}

export function resetExitPressure(game) {
  game._roomElapsedSeconds = 0;
  game._exitPressureSpawnClock = BASE_PRESSURE_INTERVAL;
  game._exitPressureSpawnCount = 0;
  game._exitObjectiveKills = 0;
  game._exitObjectiveCompleted = false;
  game._exitPressureActive = isRegularExitPressureRoom(game);
  game._exitObjectiveKillTarget = game._exitPressureActive ? requiredKillsForRoom(game) : 0;

  game.hud?.updateRoomTimer?.(0, game._exitPressureActive);
  if (!game._exitPressureActive) {
    game.hud?.setObjectiveProgress?.({ show: false });
    return;
  }

  installExitDoorGate(game);
  game.hud?.setObjectiveProgress?.({
    text: 'Defeat enemies to unlock the exit',
    hint: 'Keep moving and clear hostiles to open the upper path.',
    current: 0,
    target: game._exitObjectiveKillTarget,
    show: true,
  });
  game.hud?.showObjectiveUpdate?.(
    `Eliminate ${game._exitObjectiveKillTarget} enemies to unlock exit`,
    'Stay mobile: strafing and dashing reduce incoming damage.'
  );
}

export function updateExitPressureTimer(game, dt) {
  if (!game?._exitPressureActive) {
    game?.hud?.updateRoomTimer?.(0, false);
    return;
  }
  game._roomElapsedSeconds = Math.max(0, (game._roomElapsedSeconds || 0) + Math.max(0, dt || 0));
  game.hud?.updateRoomTimer?.(game._roomElapsedSeconds, true);
}

export function updateExitPressureSpawns(game, dt) {
  if (!game?._exitPressureActive || game._exitObjectiveCompleted || !game.player?.alive || !game.room?.door) return;

  let alivePressureEnemies = 0;
  let aliveEnemies = 0;
  for (const enemy of game.enemies) {
    if (!enemy?.alive) continue;
    aliveEnemies++;
    if (enemy._exitPressureSpawn) alivePressureEnemies++;
  }
  const maxPressureEnemies = pressureAliveCap(game);
  const maxTotalEnemies = totalEnemyCap(game);
  if (alivePressureEnemies >= maxPressureEnemies) return;
  if (aliveEnemies >= maxTotalEnemies) return;

  const elapsed = game._roomElapsedSeconds || 0;
  const rateMultiplier = Math.pow(
    PRESSURE_RATE_STEP_MULTIPLIER,
    Math.floor(elapsed / PRESSURE_RATE_STEP_SECONDS)
  );
  const interval = Math.max(MIN_PRESSURE_INTERVAL, BASE_PRESSURE_INTERVAL / rateMultiplier);

  game._exitPressureSpawnClock = (game._exitPressureSpawnClock || 0) + Math.max(0, dt || 0);
  if (game._exitPressureSpawnClock < interval) return;
  game._exitPressureSpawnClock %= interval;

  const batchSize = pressureBatchSize(elapsed);
  const availablePressureSlots = maxPressureEnemies - alivePressureEnemies;
  const availableTotalSlots = maxTotalEnemies - aliveEnemies;
  const spawnCount = Math.max(1, Math.min(batchSize, availablePressureSlots, availableTotalSlots));

  for (let i = 0; i < spawnCount; i++) {
    const type = pickExitPressureEnemyType(game, elapsed);
    const pos = pickExitPressureSpawn(game, type);
    if (!pos) return;

    const enemy = game._addEnemyByType(type, pos.x, pos.z);
    if (!enemy) return;
    enemy._exitPressureSpawn = true;
    enemy.notifyPlayerDetected?.(game.player.x, game.player.z, 3.5);
    game._exitPressureSpawnCount = (game._exitPressureSpawnCount || 0) + 1;
  }
}

export function registerExitObjectiveKill(game) {
  if (!game?._exitPressureActive || game._exitObjectiveCompleted) return false;
  const target = Math.max(1, game._exitObjectiveKillTarget || 0);
  game._exitObjectiveKills = Math.min(target, (game._exitObjectiveKills || 0) + 1);
  game.hud?.setObjectiveProgress?.({
    text: 'Defeat enemies to unlock the exit',
    hint: 'Hold pressure and clear the route to the objective marker.',
    current: game._exitObjectiveKills,
    target,
    show: true,
  });
  if (game._exitObjectiveKills < target) return false;

  game._exitObjectiveCompleted = true;
  game.room?.unlockDoor?.();
  game.audio?.play?.('doorOpen');
  game.hud?.setObjectiveProgress?.({
    text: 'Objective complete - exit unlocked',
    hint: 'Proceed through the open exit door at the top path.',
    current: target,
    target,
    show: true,
  });
  game.hud?.showObjectiveUpdate?.(
    'Objective complete - exit unlocked',
    'Proceed through the open exit door at the top path.'
  );
  return true;
}

export function pickExitPressureSpawn(game, type) {
  const door = game.room?.door;
  if (!door) return null;

  const radius = Math.max(0.4, game._radiusForEnemyType?.(type) || 0.8);
  const rng = game.rng || Math.random;
  const baseAngle = rng() * Math.PI * 2;
  const attempts = PRESSURE_SPAWN_RADII.length * 10;

  for (let i = 0; i < attempts; i++) {
    const ringRadius = PRESSURE_SPAWN_RADII[i % PRESSURE_SPAWN_RADII.length];
    const angle = baseAngle + i * 2.399963229728653;
    const x = door.x + Math.cos(angle) * ringRadius;
    const z = door.z + Math.sin(angle) * ringRadius;
    if (isExitPressureSpawnValid(game, x, z, radius)) return { x, z };
  }

  const fallback = game._findSafeSummonSpawn?.(type, door.x, door.z, {
    minPlayerDistance: PRESSURE_MIN_PLAYER_DISTANCE,
  });
  if (fallback && isExitPressureSpawnValid(game, fallback.x, fallback.z, radius, { requireLineToPlayer: false })) {
    return fallback;
  }
  return null;
}

function installExitDoorGate(game) {
  const room = game.room;
  if (!room || typeof room.unlockDoor !== 'function' || room._exitPressureOriginalUnlockDoor) return;

  const originalUnlockDoor = room.unlockDoor.bind(room);
  room._exitPressureOriginalUnlockDoor = originalUnlockDoor;
  room.unlockDoor = () => {
    if (game._exitPressureActive && !game._exitObjectiveCompleted) return;
    return originalUnlockDoor();
  };
}

function requiredKillsForRoom(game) {
  const roomNumber = Math.max(1, (game?.roomIndex ?? 0) + 1);
  if (roomNumber <= ROOM_KILL_TARGETS.length) return ROOM_KILL_TARGETS[roomNumber - 1];
  const extraRooms = roomNumber - ROOM_KILL_TARGETS.length;
  return ROOM_KILL_TARGETS[ROOM_KILL_TARGETS.length - 1] + extraRooms * 20;
}

function pressureAliveCap(game) {
  const roomNumber = Math.max(1, (game?.roomIndex ?? 0) + 1);
  const timeTier = Math.floor((game?._roomElapsedSeconds || 0) / PRESSURE_RATE_STEP_SECONDS);
  const roomBonus = Math.floor((roomNumber - 1) / 2) * 2;
  return Math.min(MAX_PRESSURE_ALIVE_CAP, BASE_PRESSURE_ALIVE_CAP + roomBonus + timeTier * 4);
}

function totalEnemyCap(game) {
  const roomNumber = Math.max(1, (game?.roomIndex ?? 0) + 1);
  const timeTier = Math.floor((game?._roomElapsedSeconds || 0) / PRESSURE_RATE_STEP_SECONDS);
  return Math.min(MAX_TOTAL_ENEMY_CAP, BASE_TOTAL_ENEMY_CAP + roomNumber + timeTier * 3);
}

function pressureBatchSize(elapsed) {
  const tier = Math.floor(Math.max(0, elapsed || 0) / PRESSURE_RATE_STEP_SECONDS);
  return Math.min(3, 1 + Math.floor((tier + 1) / 2));
}

function pickExitPressureEnemyType(game, elapsed) {
  const tier = Math.floor(elapsed / PRESSURE_RATE_STEP_SECONDS);
  const pool = tier <= 0
    ? ['drone', 'drone', 'minmon']
    : PRESSURE_SPAWN_TYPES.slice(0, Math.min(PRESSURE_SPAWN_TYPES.length, 3 + tier));
  return game.rng?.pick?.(pool) || pool[Math.floor(Math.random() * pool.length)];
}

function isExitPressureSpawnValid(game, x, z, radius, opts = {}) {
  const { requireLineToPlayer = true } = opts;
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  if (game.room?.isWalkableSpawn && !game.room.isWalkableSpawn(x, z, radius)) return false;
  if (!game._isSpawnPointClear?.(x, z, radius, { minPlayerDistance: PRESSURE_MIN_PLAYER_DISTANCE })) return false;
  if (!requireLineToPlayer || !game.player || !Array.isArray(game.room?.walls)) return true;
  return segmentVsAabbs(x, z, game.player.x, game.player.z, game.room.walls) === null;
}
